import { quoteServiceMappings } from '../inbox/quoteServiceMappings';
import { standardQuerySerializer } from '../api/serializers';
import { accountServiceMappings } from './accountServiceMappings';
import { ProductTypes } from '../types';
import { applyQueryConfig, makeCancelable, ServiceHandler } from '../utils';

const accountParamsSerializer = (params: any) =>
  standardQuerySerializer(applyQueryConfig(accountServiceMappings, params));

/**
 * New location src/api/accounts.api.ts
 * @deprecated
 */
const fetchAccountStatsHandler = ServiceHandler({
  url: 'api/account/v1/account/stats/status',
  paramsSerializer: accountParamsSerializer,
});
export const fetchAccountStats = (params: Record<string, unknown> = {}) =>
  fetchAccountStatsHandler({ params });

const fetchAgencyBillStatsHandler = ServiceHandler({
  url: '/api/billing/v1/agencyBilledInvoices/stats',
  paramsSerializer: accountParamsSerializer,
});
export const fetchAgencyBillStats = (params: Record<string, unknown> = {}) =>
  fetchAgencyBillStatsHandler({ params });

/**
 * New location src/api/accounts.api.ts
 * @deprecated
 */
const fetchQuickQuoteStatsHandler = ServiceHandler({
  url: 'api/account/v1/account/quick-quote-premium',
  paramsSerializer: accountParamsSerializer,
});
export const fetchQuickQuoteStats = (params: Record<string, unknown> = {}) =>
  fetchQuickQuoteStatsHandler({ params });

const assignAgencyHandler = ServiceHandler({
  url: 'api/auth/v1/accounts/:accountId/assign',
  method: 'put',
});
export function assignAgency(accountId: any, agencyDetails: any) {
  return assignAgencyHandler(accountId, { data: agencyDetails });
}

const markFavAccountHandler = ServiceHandler({
  url: 'api/auth/v1/accounts/:accountId/favorite/:flag',
  method: 'put',
});
export function markFavAccount(accountId: any, flag: any) {
  return markFavAccountHandler(accountId, flag);
}

const syncLeadsHandler = ServiceHandler({
  url: 'api/account/v1/account/sync/lead/:accountId',
  method: 'put',
});
export function syncLeads(accountId: any) {
  return syncLeadsHandler(accountId);
}

const fetchP100CoveragesHandler = ServiceHandler({
  url: 'api/account/v1/state/p100',
});
export function fetchP100Coverages() {
  return fetchP100CoveragesHandler();
}

const fetchP250CoveragesHandler = ServiceHandler({
  url: '/api/account/v1/state/p250',
});
export function fetchP250Coverages() {
  return fetchP250CoveragesHandler();
}

export const getCoveragesByProduct = ServiceHandler({
  url: '/api/account/v1/state/:productType',
});

const fetchPagedAccountsHandler = makeCancelable(
  ServiceHandler({
    url: '/api/account/v1/account/search',
    paramsSerializer: accountParamsSerializer,
  })
);
export const fetchPagedAccounts = (params: Record<string, unknown> = {}) =>
  fetchPagedAccountsHandler({ params });

/**
 * @name fetchAccountDetailsById
 * @deprecated
 * * see accounts.api.ts for up to date fn
 */
const fetchAccountDetailsByIdHandler = ServiceHandler({
  url: 'api/account/v1/account/:accountId',
});
export function fetchAccountDetailsById(accountId: any) {
  return fetchAccountDetailsByIdHandler(accountId);
}

export function fetchAccountDetailsByIdForP100(accountId: any) {
  return fetchAccountDetailsByIdHandler(accountId, {
    params: { product: ProductTypes.p100 },
  });
}

const reloadRiskDetailsHandler = ServiceHandler({
  url: '/api/risk/v1/reload/:cbid/:accountId',
});
export function reloadRiskDetails(accountId: any, cbid: any) {
  return reloadRiskDetailsHandler(cbid, accountId);
}

const fetchAccountRiskDetailsHandler = ServiceHandler({
  url: '/api/risk/v1/:accountId',
});
export function fetchAccountRiskDetails(accountId: any) {
  return fetchAccountRiskDetailsHandler(accountId);
}

const fetchAccountQuoteDetailsByIdHandler = ServiceHandler({
  url: 'api/quote/v1/search',
});
export function fetchAccountQuoteDetailsById(params = {}, accountId: any) {
  const finalParams = applyQueryConfig(quoteServiceMappings, params);
  return fetchAccountQuoteDetailsByIdHandler({
    params: { 'accountId.keyword': accountId, ...finalParams },
  });
}

const updateAccountContactHandler = ServiceHandler({
  url: 'api/account/v1/account/contact',
  method: 'put',
});
export function updateAccountContact(account: any) {
  return updateAccountContactHandler({ data: account });
}

const addNewAccountHandler = ServiceHandler({
  url: '/api/account/v1/account',
  method: 'post',
});
export function addNewAccount(account: any) {
  return addNewAccountHandler({ data: account });
}

const updateSecurityQuestionsHandler = ServiceHandler({
  url: '/api/account/v1/questions',
  method: 'put',
});
export const updateSecurityQuestions = (
  _params: Record<string, unknown> = {},
  data?: unknown
) => updateSecurityQuestionsHandler({ data });

const fetchUsersByAccountHandler = ServiceHandler({
  url: '/api/auth/v1/accounts/users',
});
export const fetchUsersByAccount = (params: Record<string, unknown> = {}) =>
  fetchUsersByAccountHandler({ params });

const deleteAccountHandler = ServiceHandler({
  url: '/api/auth/v1/accounts/:accountId',
  method: 'delete',
});
export function deleteAccount(accountId: any) {
  return deleteAccountHandler(accountId);
}

const reloadAccountHandler = ServiceHandler({
  url: '/api/account/v1/account/reload/:accountId',
});
export function refreshFirmographicData(accountId: any) {
  return reloadAccountHandler(accountId);
}

export function refreshCustomerFirmographicData(accountId: any) {
  return reloadAccountHandler(accountId);
}

const onDemandScanHandler = ServiceHandler({
  url: '/api/account/v1/account/scan/:accountId',
  method: 'put',
});
export function onDemandScan(accountId: any) {
  return onDemandScanHandler(accountId);
}

const deleteAccountAgencyHandler = ServiceHandler({
  url: '/api/auth/v1/accounts/:accountId/unlink',
  method: 'put',
});
export function deleteAccountAgency(accountId: any) {
  return deleteAccountAgencyHandler(accountId);
}

/**
 * verify entire account
 */
const verifyOrgProfileAllHandler = ServiceHandler({
  url: '/api/account/v1/account/profile/verify/all',
  method: 'put',
});
export function verifyOrgProfileAll(details: any) {
  return verifyOrgProfileAllHandler({ data: details });
}

const updateOrgProfileHandler = ServiceHandler({
  url: '/api/account/v1/account/profile',
  method: 'put',
});
export const updateOrgProfile = (
  _params: Record<string, unknown> = {},
  data?: unknown
) => updateOrgProfileHandler({ data });

/**
 * @param {String} accountId
 * @param {String} agencyId
 * @param {String} docName - name of the affidavit file
 * @param {FormData} file - contains data about the affidavit file
 */
const getAffidavitInfoHandler = ServiceHandler({
  url: '/api/docs/v1/upload/doc',
  method: 'post',
});
export const getAffidavitInfo = (
  _params: Record<string, unknown> = {},
  data?: unknown
) => getAffidavitInfoHandler({ data });

/**
 * @param {Object} payload
 * @param {String} payload.accountId
 * @param {String} payload.agencyId
 * @param {Object} payload.affidavit - info about the affidavit file to be uploaded
 */
const uploadAffidavitHandler = ServiceHandler({
  url: '/api/account/v1/account/affdavit/upload',
  method: 'post',
});
export function uploadAffidavit(payload: any) {
  return uploadAffidavitHandler({ data: payload });
}

const verifyOrgProfileHandler = ServiceHandler({
  url: '/api/account/v1/account/profile/verify',
  method: 'put',
});
export const verifyOrgProfile = (
  _params: Record<string, unknown> = {},
  data?: unknown
) => verifyOrgProfileHandler({ data });

const getAutocompleteAccountsHandler = ServiceHandler({
  // Formerly fir-nad (api/nad/v1/search); now served by fir-account, backed by MasterAccount.
  url: 'api/account/v1/masteraccount/accounts',
  paramsSerializer: accountParamsSerializer,
});
export const getAutocompleteAccounts = (params: Record<string, unknown> = {}) =>
  getAutocompleteAccountsHandler({ params });

const getAgentsInterestedListHandler = ServiceHandler({
  url: '/api/account/v1/agency/agencies',
});
export function getAgentsInterestedList() {
  return getAgentsInterestedListHandler();
}

const assignAccountHandler = ServiceHandler({
  url: '/api/auth/v1/accounts/:accountId/assign',
  method: 'put',
});
export function assignAccount(accountId: any, payload: any) {
  return assignAccountHandler(accountId, { data: payload });
}

const updateSocialEnggHandler = ServiceHandler({
  url: '/api/account/v1/account/profile/socialEngineering',
  method: 'put',
});
export const updateSocialEngg = (
  _params: Record<string, unknown> = {},
  data?: unknown
) => updateSocialEnggHandler({ data });

const getSocialEnggHandler = ServiceHandler({
  url: '/api/account/v1/account/profile/socialEngineering/:accountId',
});
export function getSocialEngg(accountId: any) {
  return getSocialEnggHandler(accountId);
}

const getMyAgentHandler = ServiceHandler({
  url: '/api/account/v1/agency/myagent',
});
export function getMyAgent() {
  return getMyAgentHandler();
}

const getAutocompleteAgencyHandler = ServiceHandler({
  url: '/api/agency/v1/location/agency/search',
});
export function getAutocompleteAgency(params = {}) {
  const finalParams = applyQueryConfig(accountServiceMappings, params);
  return getAutocompleteAgencyHandler({ params: finalParams });
}

const resquestAccountAccessHandler = ServiceHandler({
  url: '/api/auth/v1/accounts/:accountId/request/access',
  method: 'put',
});
export function resquestAccountAccess(accountId: any) {
  return resquestAccountAccessHandler(accountId);
}

const certifyAccountHandler = ServiceHandler({
  url: '/api/account/v1/account/profile/certify',
  method: 'put',
});
export function certifyAccount(payload: any) {
  return certifyAccountHandler({ data: payload });
}

const bulkUploadHandler = ServiceHandler({
  url: '/api/bus/v1/bus/upload',
  method: 'post',
});
export function bulkUpload(data: any, tags: any, checkbox: any, team: any) {
  return bulkUploadHandler({
    params: {
      tags,
      addRedAccounts: checkbox,
      teamId: team.id,
      teamName: team.name,
    },
    data,
  });
}

const updateProductTypeHandler = ServiceHandler({
  url: 'api/account/v1/account/product/:accountId/:productType',
  method: 'put',
});
export function updateProductType(accountId: any, productType: any) {
  return updateProductTypeHandler(accountId, productType);
}

const sendPaymentReminderHandler = ServiceHandler({
  url: '/api/billing/v1/invoiceReminder/:policyId',
  method: 'post',
});
export function sendPaymentReminder(policyId: any) {
  return sendPaymentReminderHandler(policyId, { data: {} });
}

const getBulkUploadSummaryHandler = makeCancelable(
  ServiceHandler({
    url: '/api/bus/v1/bus',
    paramsSerializer: (p) =>
      standardQuerySerializer(
        applyQueryConfig(
          [
            { path: 'page' },
            { path: 'size' },
            { path: 'search' },
            {
              paramKey: 'sort',
              transform: ({ orderBy, order }: any) => {
                switch (orderBy) {
                  case 'id':
                  case 'numUploaded':
                  case 'numSuccess':
                  case 'numFailure':
                  case 'uploadTime':
                    return `${orderBy},${order}`;
                  default:
                    return orderBy ? `${orderBy}.keyword,${order}` : null;
                }
              },
            },
          ],
          p
        )
      ),
  })
);
export const getBulkUploadSummary = (params: Record<string, unknown> = {}) =>
  getBulkUploadSummaryHandler({ params });

const getBulkUploadErrorsHandler = ServiceHandler({
  url: `/api/bus/v1/bus/:id`,
});
export const getBulkUploadErrors = ({ id }: { id: string | number }) =>
  getBulkUploadErrorsHandler(id);

const deleteBulkUploadErrorsHandler = ServiceHandler({
  url: `/api/bus/v1/bus/:id`,
  method: 'delete',
});
export const deleteBulkUploadErrors = ({ id }: { id: string | number }) =>
  deleteBulkUploadErrorsHandler(id);

const downloadAWSDocHandler = ServiceHandler({ url: '' });
export function downloadAWSDoc(url: any) {
  return downloadAWSDocHandler({ url, responseType: 'blob' });
}

/**
 * Fetch the Additional Named Insured Affidavit document
 * @returns {Object} info about the ANI affidavit document. Download link is in Object.data
 */
const getAniTemplateHandler = ServiceHandler({
  url: '/api/docs/v1/template?type=additionalInsured',
  method: 'get',
});
export const getAniTemplate = (params: Record<string, unknown> = {}) =>
  getAniTemplateHandler({ params });

const getTagsHandler = ServiceHandler({ url: `/api/account/v1/tags` });
export const getTags = (params: Record<string, unknown> = {}) =>
  getTagsHandler({ params });

const createTagHandler = ServiceHandler({
  url: '/api/account/v1/tags',
  method: 'post',
});
export const createTag = (
  _params: Record<string, unknown> = {},
  data?: unknown
) => createTagHandler({ data });

const deleteTagHandler = ServiceHandler({
  url: '/api/account/v1/tags',
  method: 'delete',
});
export const deleteTag = (
  _params: Record<string, unknown> = {},
  data?: unknown
) => deleteTagHandler({ data });

const addTagToExistingAccountHandler = ServiceHandler({
  url: '/api/account/v1/account/tags/:accountId',
  method: 'post',
});
export const addTagToExistingAccount = (
  { accountId }: { accountId: string | number },
  data?: unknown
) => addTagToExistingAccountHandler(accountId, { data });

const createNewAccountHandler = ServiceHandler({
  url: 'api/account/v1/account',
  method: 'post',
});
export const createNewAccount = (
  _params: Record<string, unknown> = {},
  data?: unknown
) => createNewAccountHandler({ data });

const acceptPrivacyConsentHandler = ServiceHandler({
  url: '/api/connector/v1/connectors/attest/:connectorType',
  method: 'put',
});
export const acceptPrivacyConsent = (
  { connectorType }: { connectorType: string },
  data?: unknown
) => acceptPrivacyConsentHandler(connectorType, { data });

const uploadAccountDocumentHandler = ServiceHandler({
  url: 'api/account/v1/account/upload',
  method: 'post',
});
export const uploadAccountDocument = (
  _params: Record<string, unknown> = {},
  data?: unknown
) => uploadAccountDocumentHandler({ data });

const getCompetitorsHandler = ServiceHandler({
  url: `/api/account/v1/account/competitors`,
});
export const getCompetitors = (params: Record<string, unknown> = {}) =>
  getCompetitorsHandler({ params });

const updateAppDetailsHandler = ServiceHandler({
  url: 'api/account/v1/account/app-details',
  method: 'post',
});
export const updateAppDetails = (
  _params: Record<string, unknown> = {},
  data?: unknown
) => updateAppDetailsHandler({ data });

const attestSurplusComplianceHandler = ServiceHandler({
  url: '/api/account/v1/agency/sign',
  method: 'put',
});
export const attestSurplusCompliance = (
  _params: Record<string, unknown> = {},
  data?: unknown
) => attestSurplusComplianceHandler({ data });

const validateDomainNameHandler = ServiceHandler({
  url: `/api/account/v1/account/domain`,
});
export const validateDomainName = ({ domain }: { domain: string }) =>
  validateDomainNameHandler({ params: { domain } });

const setFEINHandler = makeCancelable(
  ServiceHandler({ url: '/api/account/v1/agency/fein/:fein', method: 'put' })
);
export const setFEIN = ({ fein, ...rest }: any = {}) =>
  setFEINHandler(fein, { params: rest });

const getAgencyLevelsHandler = ServiceHandler({
  url: '/api/account/v1/agency/agency-levels',
});
export const getAgencyLevels = (params: Record<string, unknown> = {}) =>
  getAgencyLevelsHandler({ params });

const getEriskKeyHandler = ServiceHandler({
  url: '/api/account/v1/account/eriskhub',
});
export const getEriskKey = (params: Record<string, unknown> = {}) =>
  getEriskKeyHandler({ params });

const getAvailableProductsHandler = ServiceHandler({
  url: '/api/account/v1/state/products ',
});
export const getAvailableProducts = (params: Record<string, unknown> = {}) =>
  getAvailableProductsHandler({ params });

const getCoveragesPerProductHandler = ServiceHandler({
  url: '/api/account/v1/state/coverages/:product',
});
export const getCoveragesPerProduct = ({ product }: any = {}) =>
  getCoveragesPerProductHandler(product);

const doesVerifyAppExistForAccountHandler = ServiceHandler({
  url: '/api/account/v1/account/verify-app/:accountId',
});
export const doesVerifyAppExistForAccount = ({
  accountId,
}: {
  accountId: string | number;
}) => doesVerifyAppExistForAccountHandler(accountId);
